'use strict';

var { parse } = require('graphql');
var gqlTag = require('graphql-tag');

var _parent = {};

let log = false;

var gql = {
  /**
   * Initializes the child module, setting up a reference to the parent SAT index
   * This allows the child module to reference all other modules without additional configuration
   *
   * @param  {object} p - The parent SAT module exported from the root index file
   */
  init: p => (_parent = p),

  /**
   * Add a convenient copy of the number of rows to the root of a graphql mutation response
   *
   * @param  {object} response - A graphql mutation response
   * @returns {object} A slightly modified version of the graphql mutation
   *                  response with the affected_rows property cloned to a
   *                  same named property at the root of the object.
   */
  injectAffectedRowsAtRoot: function (response) {
    let ret = response;
    let affectedRows = 0;
    try {
      if (response && response.data) {
        const keys = Object.keys(response.data);
        if (keys.length > 0) {
          affectedRows = response.data[keys[0]].affected_rows;
        }
      }
      ret.affected_rows = affectedRows ? affectedRows : 0;
      return ret;
    } catch {
      return response;
    }
  },

  /**
   * Modifies the response object of an Apollo request, removing the named property referencing the queried database table
   * and injected the values up one level into the 'data' object instead.
   *
   * @param  {object} response - A graphql mutation response
   * @returns {object} A slightly modified version of the graphql mutation
   *                  response without the named child property inside of the data object.
   */
  injectNestedDataAsData: function (response) {
    let ret = response;
    try {
      if (response && response.data) {
        const keys = Object.keys(response.data);
        if (keys.length === 1) {
          if (response.data[keys[0]].returning) {
            response.data = response.data[keys[0]].returning;
          } else {
            response.data = response.data[keys[0]];
          }
          delete response.data[keys[0]];
        }
        if(keys.length > 1){
          for(var i = 0; i <= keys.length; i++){
            if (response.data[keys[i]].returning) {
              response.data[keys[i]] = response.data[keys[i]].returning;
            } else {
              response.data[keys[i]] = response.data[keys[i]];
            }
          }
        }
      }
      return ret;
    } catch {
      return response;
    }
  },

  /**
   * Performs a GraphQL query, modifying the response object before returning it to the client.
   *
   * @param  {gql} query - The GraphQL query to execute.
   * @param  {object} variables - Variables to pass with the query.
   * @returns {object} The modified query response.
   */
  query: async function (query, variables) {
    try {
      // Build and return a response object from the modified GQL response
      return _parent.gql.buildReturnObjectFromApollo(
        // Modify the 'data' object to hold the returned data rather than a nested obj property
        _parent.gql.injectNestedDataAsData(
          // Perform the GQL query and return the response
          await _parent.gql.queryWithApollo(
            typeof query === 'string' ? parse(query) : query,
            variables,
            _parent.config.apollo_client
          )
        )
      );
    } catch (error) {
      return _parent.gql.buildReturnObjectFromApollo({
        errors: error.graphQLErrors || [error],
      });
    }
  },

  /**
   * Performs a GraphQL mutation, modifying the response object before returning it to the client.
   *
   * @param  {gql} mutation - The GraphQL mutation to execute.
   * @param  {object} variables - Variables to pass with the mutation.
   * @returns {object} The modified mutation response.
   */
  mutation: async function (query, variables) {
    try {
  // Build and return a response object from the modified GQL response
      return _parent.gql.buildReturnObjectFromApollo(
        // Modify the 'data' object to hold the returned data rather than a nested obj property
        _parent.gql.injectNestedDataAsData(
          // Pull out the 'affected_rows' property and make it available at the root of the response obj
          _parent.gql.injectAffectedRowsAtRoot(
            // Perform the GQL mutation and return the response
            await _parent.gql.mutationWithApollo(
              (typeof query === 'string' ? parse(query) : query),
              variables,
              _parent.config.apollo_client
            )
          )
        )
      );
    } catch (error) {
      return _parent.gql.buildReturnObjectFromApollo({
        errors: error.graphQLErrors || [error],
      });
    }
  },

  /**
   * Executes a GraphQL query using an Apollo Client
   *
   * @param  {gql} query - The GraphQL query to execute.
   * @param  {object} variables - Variables to pass with the query.
   * @returns {object} The query response.
   */
  queryWithApollo: async (query, variables, apolloClient) => {
    if (!apolloClient) {
      throw 'No apollo client found';
    }
    return await apolloClient.query({
      query: query,
      variables: variables,
    });
  },

  /**
   * Executes a GraphQL mutation using an Apollo Client
   *
   * @param  {gql} mutation - The GraphQL mutation to execute.
   * @param  {object} variables - Variables to pass with the mutation.
   * @returns {object} The mutation response.
   */
  mutationWithApollo: async (mutation, variables, apolloClient) => {
    if (!apolloClient) {
      throw 'No apollo client found';
    }
    return await apolloClient.mutate({
      mutation: mutation,
      variables: variables,
    });
  },

  /**
   * Builds a formatted string of variables and their data types to pass into a GQL query or mutation
   *
   * @param  {string} table - The name of the database table being queried, e.g. "locations"
   * @param  {object} variables - Variables to pass with the query.
   * @returns {string} The formatted variables string to be injected into a GQL query or mutation
   */
  buildVariablesDeclarationString: (table, variables) => {
    if (!table || !variables) return '';
    else {
      let variablesString = '';
      for (let key in variables) {
        variablesString += `$${key}: ${_parent.types[table][key]},`; // prettier-ignore
      }
      variablesString = `(${variablesString})`;
      return variablesString;
    }
  },

  /**
   * Builds a formatted where clause from variables to pass into a GQL query or mutation.
   * The where clause will be a concatenated string of equal to operators, e.g. '(where: {region_id: {_eq: 2}})'
   *
   * @param  {object} variables - Variables to use in a where clause
   * @returns {string} The formatted where clause to be injected into a GQL query or mutation
   */
  buildWhereClauseString: variables => {
    if (!variables) return '';
    else {
      let whereClauseString = '';
      for (let [key, value] of Object.entries(variables)) {
        whereClauseString += `${key}: {_eq: ${typeof value === "string" ? `"${value}"` : value}},`; // prettier-ignore
      }
      return `(where: {${whereClauseString}})`;
    }
  },

  /**
   * Builds a formatted update (_set) clause from variables to pass into a GQL mutation.
   * The update clause will be a concatenated string of key-value pairs, e.g. '{numeric_field: 1, string_field: "2"}'
   *
   * @param  {object} variables - Variables to use in a where clause
   * @returns {string} The formatted update clause to be injected into a GQL mutation
   */
  buildUpdateClauseString: variables => {
    if (!variables) return '{}';
    else {
      let updateClauseString = '';
      for (let [key, value] of Object.entries(variables)) {
        updateClauseString += `${key}: ${typeof value === "string" ? `"${value}"` : value},`; // prettier-ignore
      }
      return updateClauseString.includes('updatedat')
        ? `{${updateClauseString}}`
        : `{${updateClauseString}updatedat: "now()",}`;
    }
  },

  /**
   * Builds a standardized response object from an Apollo Client request to return to the client
   *
   * @param  {Object} apolloResponse - The response received from GQL by an Apollo Client request
   * @return {Object}  return standard response object
   */
  buildReturnObjectFromApollo: async response => {
    let apolloResponse = await response;
    log && console.log(apolloResponse);
    try {
      if (!apolloResponse) {
        // No response object returned from apollo
        return {
          success: false,
          message: 'No response from Apollo Client request',
          status_code: 500,
          data: null,
          errors: null,
          affected_rows: 0,
        };
      } else if (apolloResponse.data) {
        // Received response with data
        if (apolloResponse.errors || apolloResponse.networkStatus === 8) {
          // Response also has errors
          return {
            success: true,
            message: 'Apollo Client request accepted with errors',
            status_code: 207,
            data: apolloResponse.data,
            errors: apolloResponse.errors,
            affected_rows: apolloResponse.affected_rows || 0,
          };
        } else {
          // Success with no errors
          return {
            success: true,
            message: 'Apollo Client request successful',
            status_code: 200,
            data: apolloResponse.data,
            errors: null,
            affected_rows: apolloResponse.affected_rows || 0,
          };
        }
      } else {
        // No data returned
        if (apolloResponse.errors || apolloResponse.networkStatus === 8) {
          // Error with request
          return {
            success: false,
            message: 'Apollo Client request failed',
            status_code: 400,
            data: null,
            errors: apolloResponse.errors,
            affected_rows: apolloResponse.affected_rows || 0,
          };
        } else {
          // Successful request with no data returned
          return {
            success: true,
            message: 'Apollo Client request successful but no data returned',
            status_code: 204,
            data: apolloResponse.data || null,
            errors: apolloResponse.errors || null,
            affected_rows: apolloResponse.affected_rows || 0,
          };
        }
      }
    } catch (error) {
      return {
        success: false,
        message: 'Unexpected error in Apollo Client Request',
        status_code: 500,
        data: apolloResponse.data || null,
        errors: [error],
        affected_rows: apolloResponse.affected_rows || 0,
      };
    }
  },
};

module.exports = gql;
